import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCountdown } from '@renderer/utils/countdownUtils'
import Button from '@renderer/components/bahela/Button'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'

// Import Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaLogo from '/assets/bahela/bahela.webp'

const RetakePage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { i18n } = useTranslation()
  const [selectedPhotos, setSelectedPhotos] = useState<number[]>([])
  const [remainingTime, setRemainingTime] = useState<number>(
    () => useConfigStore.getState().config?.pages.retake?.settings?.retakeTimeout || 20
  )

  const { config } = useConfigStore()
  const { gallery, setSessionState, uploadSessionState, userPreferences } = useSessionStateStore()
  const { cameraSettings } = useSettingsStore()

  // Helper function to normalize captureRatio - 'auto' means no cropping
  const getNormalizedCaptureRatio = (): number => {
    if (cameraSettings.captureRatio === 'auto') return 1 // Default to 1:1 for auto
    if (typeof cameraSettings.captureRatio === 'number') return cameraSettings.captureRatio
    return 1 // Default fallback
  }

  const ratio = getNormalizedCaptureRatio()
  const containerSize = 269 // Base size in pixels
  const containerStyle = {
    width: ratio >= 1 ? containerSize : containerSize * ratio,
    height: ratio >= 1 ? containerSize / ratio : containerSize
  }

  const maxRetakeCount = config?.pages.retake?.settings?.maxRetakeCount || 3
  const retakeTimeout = config?.pages.retake?.settings?.retakeTimeout || 20

  // Initialize countdown hook
  const retakeCountdown = useCountdown(retakeTimeout)

  // Filter out only items with photoUrl
  const validPhotos = gallery.capturedItems.filter((item) => item.photoUrl)

  useEffect((): (() => void) => {
    retakeCountdown.setOnTick(() => {
      setRemainingTime(retakeCountdown.getRemainingSeconds())
    })

    retakeCountdown.setOnComplete(() => {
      continueToNextPage()
    })

    retakeCountdown.start()

    return () => {
      retakeCountdown.stop()
    }
  }, [])

  const handlePhotoClick = (index: number): void => {
    setSelectedPhotos((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index)
      } else if (prev.length < maxRetakeCount) {
        return [...prev, index]
      }
      return prev
    })
  }

  const handleRetake = async (): Promise<void> => {
    const remainingItems = validPhotos.filter((_, index) => !selectedPhotos.includes(index))

    // Navigate to capture page for retakes
    navigate(`/${projectId}/capture`)

    // Update session state with remaining photos
    await setSessionState({
      userPreferences: {
        retakeCount: selectedPhotos.length,
        retakeSessionsCompleted: userPreferences.retakeSessionsCompleted + 1
      },
      gallery: {
        capturedItems: remainingItems
      }
    })

    // Upload session state before navigating
    uploadSessionState()
  }

  const continueToNextPage = async (): Promise<void> => {
    uploadSessionState()
    navigate(`/${projectId}/frame`)
  }

  // Call all hooks at the top level
  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedBahelaLogo = useCachedImage(bahelaLogo)

  return (
    <div className="relative bg-black w-full h-full overflow-hidden">
      {/* Background Layer */}
      <div
        className="absolute top-0 left-0 w-full h-full bg-cover z-[0]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      />

      {/* Main Content */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4/5 flex flex-col gap-[51px]">
        {/* Title */}
        <h1 className="font-serif-bahela text-center text-[60px] text-white">
          {i18n.language === 'id' ? 'Pilih ' : 'Choose '}
          <span className="text-uriYellow">
            {i18n.language === 'id'
              ? `maksimal ${maxRetakeCount} Foto`
              : `maximum of ${maxRetakeCount} photos`}
          </span>
          {i18n.language === 'id' ? ' untuk diulang' : ' to retake'}
        </h1>

        {/* Photo Grid */}
        <div className="w-full h-[601px] grid grid-cols-3 gap-8 px-64 place-items-center">
          {validPhotos.map((item, index) => {
            const isSelected = selectedPhotos.includes(index)
            return (
              <div
                key={index}
                className="relative"
                style={{
                  width: containerStyle.width,
                  height: containerStyle.height
                }}
              >
                <div
                  className={`absolute inset-0 ${
                    isSelected ? 'border-[8px] rounded-xl border-uriYellow' : ''
                  }`}
                >
                  <CachedRetakePhoto
                    photoUrl={item.photoUrl}
                    index={index}
                    onPhotoClick={handlePhotoClick}
                  />
                </div>
                {isSelected && (
                  <div className="absolute bottom-4 right-4 drop-shadow-2xl w-[34px] h-[34px] rounded-full border-2 bg-grey border-uriYellow flex items-center justify-center z-10">
                    <span className="text-uriYellow text-xl">✓</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Action Buttons */}
        <div className="w-full flex gap-[46px] justify-center">
          <Button
            onClick={handleRetake}
            className={`text-[49px] z-[2] ${selectedPhotos.length === 0 ? 'opacity-30' : 'opacity-100'}`}
            textColor="methadone"
            disabled={selectedPhotos.length === 0}
          >
            {i18n.language === 'id' ? 'Foto Ulang' : 'Retake'}
          </Button>
          <Button
            onClick={continueToNextPage}
            className={`text-[49px] z-[2] ${selectedPhotos.length === 0 ? 'opacity-100' : 'opacity-30'}`}
            textColor="methadone"
            disabled={selectedPhotos.length > 0}
          >
            {i18n.language === 'id' ? 'Lanjut ' : 'Next '} ({remainingTime})
          </Button>
        </div>
      </div>

      {/* Bahela Logo */}
      <img
        src={cachedBahelaLogo}
        alt="Bahela Logo"
        className="absolute z-[1] bottom-[66px] left-[57px] w-[127px] h-[114px]"
      />
    </div>
  )
}

// Component for cached retake photos
const CachedRetakePhoto: React.FC<{
  photoUrl: string
  index: number
  onPhotoClick: (index: number) => void
}> = ({ photoUrl, index, onPhotoClick }) => {
  const cachedSrc = useCachedImage(photoUrl)
  return (
    <img
      src={cachedSrc}
      className="w-full h-full cursor-pointer btn-shadow rounded-[4px]"
      style={{ objectFit: 'cover' }}
      onClick={() => onPhotoClick(index)}
      alt={`Captured ${index + 1}`}
    />
  )
}

export default RetakePage
